import React, { useContext, useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  createContactsV2,
  enrichProfile,
  getContactDetailsById,
  getEnrichedDataByContactId,
  getEnrichedDataByPersonId,
  getIntegrations,
} from "../../apis/ext";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Skeleton } from "../ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ChevronDownIcon, ChevronUpIcon, ReloadIcon } from "@radix-ui/react-icons";
import EnrichedPhone from "./EnrichedPhone";
import EnrichedEmail from "./EnrichedEmail";
import TooltipWrapper from "./TooltipWrapper";
import StripeLoader from "./StripeLoader";
import ICPRating from "./ICPRating";
import CrmContacts from "./CrmContacts";
import SharedDataContext from "../../SharedDataContext";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { restrictedIntegrations } from "../../axios/constant";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
import { Checkbox } from "../ui/checkbox";
import { isMoreThanDaysOld } from "../../cf";
import Image from "../Tags/Image";
import IcpPersona from "../containers/Scoring/IcpPersona";
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

export default function Contact({
  getProfileData,
  crmPhonesEmails = { phone_numbers: [], email_addresses: [] },
  getEnrichedTimeline = null,
}) {
  const isPremiumProvidersEnabled = useFeatureFlag("enable-premium-providers");
  const isAdvancePhoneVerificationEnabled = useFeatureFlag("advanced-phone-verification");
  const captureEvent = useCaptureEvent();
  const { userData, params } = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const [enrichedData, setEnrichedData] = useState({});

  // create custom event to send contact revealed.

  const [isContactRevealedEventGenerated, setIsContactRevealedEventGenerated] = useState(false);
  const generateContactRevealedEvent = () => {
    const newevent = new CustomEvent("contact_revealed", {
      detail: true,
    });
    window.dispatchEvent(newevent);

    let contact_details_found = {
      work_email: false,
      personal_email: false,
      direct_phone: false,
      mobile_phone: false,
      hq_phone: false,
    };

    if (enrichedData && enrichedData.email_addresses && enrichedData.email_addresses.length > 0) {
      contact_details_found.work_email = enrichedData.email_addresses.some((e) => e.type === "Work");
      contact_details_found.personal_email = enrichedData.email_addresses.some((e) => e.type === "Personal");
    }
    if (enrichedData && enrichedData.phone_numbers && enrichedData.phone_numbers.length > 0) {
      contact_details_found.direct_phone = enrichedData.phone_numbers.some((e) => e.type === "Direct");
      contact_details_found.mobile_phone = enrichedData.phone_numbers.some((e) => e.type === "Mobile");
      contact_details_found.hq_phone = enrichedData.phone_numbers.some((e) => e.type === "Work HQ");
    }
    console.log("contact_details_found", contact_details_found);
    captureEvent("EXP_Person_RevealContactBtn", {
      contact_details_found: contact_details_found,
    });
    setIsContactRevealedEventGenerated(true);
  };

  // enrich profile by contact id

  const [enrichmentMode, setEnrichmentMode] = useState([
    "mobile_phone",
    "direct_phone",
    "work_email",
    "personal_email",
  ]);
  const [enrichmentModeEmail, setEnrichmentModeEmail] = useState(true);
  const [enrichmentModePhone, setEnrichmentModePhone] = useState(true);
  const [enrichmentModeSingle, setEnrichmentModeSingle] = useState(false);

  useEffect(() => {
    if (!enrichmentModeSingle) {
      if (enrichmentModePhone && enrichmentModeEmail) {
        setEnrichmentMode(["mobile_phone", "direct_phone", "work_email", "personal_email"]);
      } else if (enrichmentModePhone && !enrichmentModeEmail) {
        setEnrichmentMode(["mobile_phone", "direct_phone"]);
      } else if (!enrichmentModePhone && enrichmentModeEmail) {
        setEnrichmentMode(["work_email", "personal_email"]);
      } else {
        setEnrichmentMode([]);
      }
    }
  }, [enrichmentModeEmail, enrichmentModePhone, enrichmentModeSingle]);

  const [contactId, setContactId] = useState("");
  const [isEnriched, setIsEnriched] = useState(false);
  const enrichProfileFn = useMutation("enrich_profile", enrichProfile, {
    onSuccess(data, variables, context) {
      console.log("data", data);

      setIsEnriched(true);
      setShowReEnrichCard(false);

      setTimeout(() => {
        setIsEnriched(false);
      }, 1000);
    },
    onError(error) {
      console.log(error);
      setIsEnriched(false);
    },
  });

  const handleReEnrichContactDetailsClick = (
    contactId,
    requirements = ["mobile_phone", "direct_phone", "work_email", "personal_email"]
  ) => {
    captureEvent("EXP_Person_ReenrichBtn");
    enrichProfileDetails(contactId, requirements);
  };

  const handleRevealContactDetailsClick = (contactId, requirements = enrichmentMode) => {
    enrichProfileDetails(contactId, requirements);

    setToggleEnrichData(true);
  };

  const enrichProfileDetails = (contactId, requirements = enrichmentMode) => {
    const payload = {
      settings: {
        verify: ["work_email", "mobile_phone", "personal_email"],
      },
      requirements: requirements,
      enrichment_type: "People",
      records: [
        {
          contact_id: contactId,
        },
      ],
    };
    console.log("payload", payload);
    enrichProfileFn.mutate({
      data: {
        ...payload,
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const getContactDetailsByIdFn = useMutation("get_contact_details", getContactDetailsById, {
    onSuccess(data, variables, context) {
      console.log("get_contact_details", data);
      let newdata = data;
      newdata.current_company.linkedin_urls = [newdata?.current_company?.linkedin_url];

      setEnrichedData({ ...newdata, fromContactId: true, fromPersonId: false });
    },
    onError(error) {
      console.log(error);
    },
  });

  const createContactsV2Fn = useMutation("enrich_profile", createContactsV2, {
    onSuccess(data, variables, context) {
      console.log("createContactsV2Fn", data);
      // toast.success(data?.message);
      if (data && data?.data && data?.data?.contacts && data?.data?.contacts.length > 0) {
        // let arr = data?.data?.contacts.map((el) => el.contact_id);
        setContactId(data?.data?.contacts[0]?.contact_id);

        handleRevealContactDetailsClick(data?.data?.contacts[0]?.contact_id);
      }
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleCreateContactsV2 = (person_id) => {
    if (createContactsV2Fn.isLoading) {
      return;
    }
    createContactsV2Fn.mutate({
      data: {
        contacts: [{ person_id: person_id }],
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  // const getEnrichedDataByContactIdFn = useMutation(
  //   "get_enrichmentData_by_contact_id",
  //   getEnrichedDataByContactId,
  //   {
  //     onSuccess(data, variables, context) {
  //       setEnrichedData(data.contact);
  //     },
  //     onError(error) {
  //       console.log(error);
  //     },
  //   }
  // );

  useEffect(() => {
    if (params.contact_id) {
      setIsEnriched(true);
      setContactId(params?.contact_id);
    }
  }, [params?.contact_id]);

  useEffect(() => {
    if (contactId && isEnriched) {
      handleGetContactDetailsById(contactId);
    }
  }, [isEnriched, contactId]);

  const handleGetContactDetailsById = (id = "") => {
    getContactDetailsByIdFn.mutate({
      id: id ? id : enrichedData?.id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const getEnrichedDataBypersonIdFn = useMutation(
    "get_enrichmentData_by_person_id",
    getEnrichedDataByPersonId,
    {
      onSuccess(data, variables, context) {
        let persondata = data.data;
        persondata.enrichment_lists = [];
        console.log("persondata", persondata);
        if (persondata?.contacts && persondata?.contacts.length === 0) {
          setEnrichedData({ ...persondata, fromContactId: false, fromPersonId: true });
        }
        if (persondata?.contacts && persondata?.contacts.length > 0) {
          handleGetContactDetailsById(persondata?.contacts?.[0]?.id);
          setContactId(persondata?.contacts?.[0]?.id);
        }
      },
      onError(error) {
        console.log(error);
      },
    }
  );

  const handleGetEnrichedDataByPersonId = () => {
    getEnrichedDataBypersonIdFn.mutate({
      id: params.person_id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  useEffect(() => {
    console.log("params", params);
    if (activeWorkspace && activeWorkspace.id && params && params.person_id) {
      handleGetEnrichedDataByPersonId();
    }

    if (typeof window !== "undefined") {
      const handleRunSearch = (event) => {
        if (activeWorkspace && activeWorkspace.id && params && params.person_id) {
          handleGetEnrichedDataByPersonId();
        }
      };
      window.addEventListener("person_record_created", handleRunSearch);
      return () => {
        window.removeEventListener("person_record_created", handleRunSearch);
      };
    }
  }, [activeWorkspace, params]);

  useEffect(() => {
    // This function is what you want to run at every interval
    const doPolling = () => {
      if (enrichedData && enrichedData.id && !enrichProfileFn.isLoading) {
        handleGetContactDetailsById();
      }
    };

    // Start polling
    const intervalId = setInterval(doPolling, 5000); // 5000ms = 5 seconds

    if (enrichedData && enrichedData.enrichment_lists && enrichedData.enrichment_lists.length > 0) {
      const hasPendingInTimeline = enrichedData.enrichment_lists.some(
        (item) => item.status.toLowerCase() === "queued" || item.status.toLowerCase() === "running"
      );
      const hasPendingInMobile =
        enrichedData.phone_numbers &&
        enrichedData.phone_numbers.length > 0 &&
        enrichedData.phone_numbers.some(
          (item) =>
            item.verification && ["running", "pending"].includes(item.verification.status.toLowerCase())
        );
      const hasPendingInEmail =
        enrichedData.email_addresses &&
        enrichedData.email_addresses.length > 0 &&
        enrichedData.email_addresses.some(
          (item) =>
            item.verification && ["running", "pending"].includes(item.verification.status.toLowerCase())
        );
      const hasPendingInMobileAdvanceVerification =
        enrichedData.phone_numbers &&
        enrichedData.phone_numbers.length > 0 &&
        enrichedData.phone_numbers.some(
          (item) =>
            item.verification &&
            item.verification.advanced_verification &&
            (item.verification.advanced_verification.status.toLowerCase() === "running" ||
              item.verification.advanced_verification.status.toLowerCase() === "queued")
        );
      if (!hasPendingInTimeline) {
        // If no items are 'Pending', stop polling
        if (!hasPendingInMobile && !hasPendingInEmail && !isContactRevealedEventGenerated) {
          generateContactRevealedEvent();
        }
        if (!hasPendingInMobile && !hasPendingInEmail && !hasPendingInMobileAdvanceVerification) {
          clearInterval(intervalId);
        }
      }
    }
    if (enrichedData && enrichedData.enrichment_lists && enrichedData.enrichment_lists.length === 0) {
      clearInterval(intervalId);
    }

    // Cleanup function to clear the interval when the component unmounts or the effect re-runs
    return () => clearInterval(intervalId);
  }, [enrichedData, activeWorkspace && activeWorkspace.id]); // Empty dependency array means this effect runs only once on mount

  // useEffect(() => {
  //   // This function is what you want to run at every interval
  //   const doPolling = () => {
  //     if (enrichedData && enrichedData.id && !enrichProfileFn.isLoading) {
  //       handleGetContactDetailsById();
  //     }
  //   };

  //   // Start polling
  //   const intervalId = setInterval(doPolling, 5000); // 5000ms = 5 seconds

  //   if (enrichedData && enrichedData.icp_ratings && enrichedData.icp_ratings.length > 0) {
  //     const hasPendingInICP =
  //       enrichedData.icp_ratings &&
  //       enrichedData.icp_ratings.length > 0 &&
  //       enrichedData.icp_ratings.some((item) => item.status && item.status.toLowerCase() === "running");
  //     if (!hasPendingInICP) {
  //       // If no items are 'Pending', stop polling
  //       clearInterval(intervalId);
  //     }
  //   }
  //   if (enrichedData && enrichedData.icp_ratings && enrichedData.icp_ratings.length === 0) {
  //     clearInterval(intervalId);
  //   }

  //   // Cleanup function to clear the interval when the component unmounts or the effect re-runs
  //   return () => clearInterval(intervalId);
  // }, [enrichedData, activeWorkspace && activeWorkspace.id]); // Empty dependency array means this effect runs only once on mount

  const [toggleEnrichData, setToggleEnrichData] = useState(false);
  const handleToggleEnrichData = () => {
    setToggleEnrichData(!toggleEnrichData);
  };
  const filterByFound = (arr) => {
    return arr.filter((obj) => obj.data_found === true);
  };

  const getUniqueRequirements = (dataArray) => {
    const requirementSet = new Set();

    dataArray.forEach((entry) => {
      entry.requirements?.forEach((req) => {
        requirementSet.add(req);
      });
    });

    return Array.from(requirementSet);
  };

  // const processSourceData = (sourceData) => {
  //   // Group data by 'integration_config.integration.key'
  //   const grouped = sourceData.reduce((acc, item) => {
  //     const key = item?.integration_config?.integration?.key;
  //     if (!acc[key]) {
  //       acc[key] = {
  //         ...item,
  //         data_found: item.status === "Success",
  //         enrichment_data: [
  //           {
  //             enrichment_mode: item.enrichment_mode,
  //             status: item.status,
  //           },
  //         ],
  //       };
  //     } else {
  //       acc[key].enrichment_data.push({
  //         enrichment_mode: item.enrichment_mode,
  //         status: item.status,
  //       });
  //       // Update data_found if any of the statuses is 'Found'
  //       if (item.status === "Success") {
  //         acc[key].data_found = true;
  //       }
  //     }
  //     return acc;
  //   }, {});

  //   // Convert the grouped data back into an array and adjust fields
  //   return Object.values(grouped).map((item) => {
  //     // Remove fields that are not required in the final output
  //     delete item.enrichment_mode;
  //     delete item.status;
  //     return item;
  //   });
  // };

  // const processSourceDataDateWise = (sourceData) => {
  //   // Helper to extract the date (YYYY-MM-DD) from the 'created_at' field
  //   const getDatePart = (dateString) => dateString.split("T")[0];

  //   // Group data by 'integration_config.integration.key' and 'created_at' date
  //   const grouped = sourceData.reduce((acc, item) => {
  //     const key = item?.integration_config?.integration?.key;
  //     const datePart = getDatePart(item.created_at);
  //     const combinedKey = `${key}_${datePart}`;

  //     if (!acc[combinedKey]) {
  //       acc[combinedKey] = {
  //         ...item,
  //         data_found: item.status === "Success",
  //         enrichment_data: [
  //           {
  //             enrichment_mode: item.enrichment_mode,
  //             status: item.status,
  //           },
  //         ],
  //       };
  //     } else {
  //       acc[combinedKey].enrichment_data.push({
  //         enrichment_mode: item.enrichment_mode,
  //         status: item.status,
  //       });

  //       // Update data_found if any of the statuses is 'Success'
  //       if (item.status === "Success") {
  //         acc[combinedKey].data_found = true;
  //       }
  //     }

  //     return acc;
  //   }, {});

  //   // Convert the grouped data back into an array and adjust fields
  //   return Object.values(grouped).map((item) => {
  //     // Remove fields that are not required in the final output
  //     delete item.enrichment_mode;
  //     delete item.status;
  //     return item;
  //   });
  // };

  const groupByIntegrationWithMapping = (actions) => {
    const grouped = {};

    const aggregateMapping = (mapping = []) => {
      const mappingTypes = [
        {
          key: "phone",
          requirements: ["mobile_phone", "direct_phone", "work_phone", "hq_phone"],
        },
        {
          key: "email",
          requirements: ["work_email", "personal_email"],
        },
      ];

      return mappingTypes
        .map(({ key, requirements }) => {
          const relevantMappings = mapping.filter((mapItem) =>
            mapItem?.requirements?.some((req) => requirements.includes(req))
          );

          if (relevantMappings.length === 0) return null;

          const hasSuccess = relevantMappings.some((item) => item.status === "Success");
          const hasError = relevantMappings.some((item) => item.status === "Error");

          const status = hasSuccess
            ? "Success"
            : hasError
              ? "Error"
              : relevantMappings[0]?.status || "No Match";

          const error_message =
            status === "Success"
              ? ""
              : relevantMappings.find((item) => item?.error_message)?.error_message || "";

          return {
            requirements: [key],
            error_message,
            status,
          };
        })
        .filter(Boolean);
    };

    actions &&
      actions.length > 0 &&
      actions.forEach((action) => {
        const key = action?.integration_config?.integration?.key;

        const mappingItem = {
          requirements: action?.requirements,
          error_message: action?.error_message,
          status: action?.status,
        };

        if (!grouped[key]) {
          // clone the original action for base and initialize mapping array
          grouped[key] = {
            ...action,
            mapping: [mappingItem],
          };
        } else {
          grouped[key]?.mapping?.push(mappingItem);
        }
      });

    const arr = Object.values(grouped).map((item) => ({
      ...item,
      mapping: aggregateMapping(item?.mapping),
    }));

    console.log("just to check", arr);

    return arr;
  };

  const [enrichedList, setEnrichedList] = useState([]);
  useEffect(() => {
    if (enrichedData && enrichedData.id) {
      console.log("enrichedData in get", enrichedData);
      getProfileData(enrichedData.external_urls, {
        first_name: enrichedData?.name?.first,
        last_name: enrichedData?.name?.last,
        full_name: enrichedData?.name?.full,
        headline:
          enrichedData?.work_experiences &&
          enrichedData?.work_experiences.length > 0 &&
          enrichedData?.work_experiences[0]?.job_title
            ? enrichedData?.work_experiences[0]?.job_title
            : enrichedData?.current_job_title,
        company_name:
          enrichedData?.current_company && enrichedData?.current_company?.name
            ? enrichedData?.current_company?.name
            : "",
        company_linkedin_url: enrichedData?.current_company?.linkedin_urls[0]
          ? enrichedData?.current_company?.linkedin_urls[0]
          : "",
        location: enrichedData?.current_location?.formatted,
        contact_id: enrichedData?.fromContactId ? enrichedData.id : "",
        person_id: enrichedData?.fromPersonId ? enrichedData?.id : "",
        organization_id:
          enrichedData?.current_company && enrichedData?.current_company?.organization_ids?.system,
      });
    }
    if (enrichedData?.enrichment_lists && enrichedData?.enrichment_lists.length > 0) {
      setEnrichedList(enrichedData?.enrichment_lists);
      getEnrichedTimeline && getEnrichedTimeline(enrichedData?.enrichment_lists);
    } else {
      getEnrichedTimeline && getEnrichedTimeline(enrichedData?.enrichment_lists);
    }
    return () => {
      setEnrichedList([]);
    };
  }, [enrichedData]);

  // get integrations
  const filterByCategory = (sourceArray, targetCategory) => {
    return sourceArray.filter(
      (obj) => Array.isArray(obj.categories) && obj.categories.includes(targetCategory) && obj.is_enabled
    );
  };

  const [integrations, setIntegrations] = useState({});
  const [installedIntegrations, setInstalledIntegrations] = useState([]);
  const [installedDataProviderIntegrations, setInstalledDataProviderIntegrations] = useState([]);
  const [toggleIntegation, setToggleIntegration] = useState(false);
  const handleToggleIntegration = () => {
    setToggleIntegration(!toggleIntegation);
  };

  const filterByInstalled = (arr) => {
    return arr.filter((obj) => obj.hasOwnProperty("installed"));
  };

  const getIntegrationsFn = useQuery(
    "get_integrations",
    () => {
      return getIntegrations({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      });
    },
    {
      enabled: !!activeWorkspace,
      refetchOnWindowFocus: false,
      manual: true,
      onSuccess: (data) => {
        const newData = data;

        if (newData.installed && newData.installed.length > 0) {
          newData.installed = newData.installed.filter((instApp) => instApp.integration.key !== "crm");
        }
        if (newData.available && newData.available.length > 0) {
          newData.available = newData.available.filter((int) => int.key !== "crm");
        }

        if (newData && newData.installed && newData.installed.length === 0) {
          setToggleIntegration(true);
        }

        newData.available.forEach((availApp) => {
          const isInstalled = newData.installed.find((instApp) => instApp.integration.key === availApp.key);
          if (isInstalled && isInstalled.id) {
            availApp.installed = true;
            availApp.is_enabled = isInstalled?.is_enabled;
          }
        });
        setIntegrations(newData);
        setInstalledIntegrations(filterByInstalled(newData.available));
        setInstalledDataProviderIntegrations(
          filterByCategory(filterByInstalled(newData.available), "Data Providers")
        );
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const handleGetIntegrations = () => {
    getIntegrationsFn.refetch();
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      handleGetIntegrations();
    }
  }, [activeWorkspace]);

  const printStatus = (status) => {
    let img = "";
    let text = status;
    if (status === "Success") {
      img = "check-green-circle";
      text = "Found";
    } else if (status === "Not Found") {
      img = "cross-red-circle";
    } else if (status === "Error") {
      img = "cross-red-circle";
    } else {
      return <span>{status}</span>;
    }
    return (
      <>
        <Image src={`/images/ext/${img}.svg`} width={12} height={12} className="mr-2 no-invert" />
        <span>{text}</span>
      </>
    );
  };

  const [showReEnrichCard, setShowReEnrichCard] = useState(false);

  const shouldShowBlur = () => {
    if (enrichedData && enrichedData?.enrichment_lists && enrichedData?.enrichment_lists.length === 0) {
      return true;
    } else if (
      enrichedData &&
      enrichedData.enrichment_lists &&
      enrichedData.enrichment_lists.some(
        (item) => item.status.toLowerCase() === "queued" || item.status.toLowerCase() === "running"
      ) &&
      filterByFound(enrichedList).length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isEnrichmentTimelineRunning = () => {
    if (
      enrichedData &&
      enrichedData.enrichment_lists &&
      enrichedData.enrichment_lists.some(
        (item) => item.status.toLowerCase() === "queued" || item.status.toLowerCase() === "running"
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  const showStripeLoader = () => {
    if (
      enrichedData &&
      enrichedData.enrichment_lists &&
      enrichedData.enrichment_lists.some(
        (item) => item.status.toLowerCase() === "queued" || item.status.toLowerCase() === "running"
      )
    ) {
      return <StripeLoader />;
    } else if (getContactDetailsByIdFn.isLoading) {
      return <StripeLoader />;
    }
  };

  const parseErrorMessage = (errorMessage) => {
    try {
      const parsedError = JSON.parse(errorMessage);
      const { status, message, error_message } = parsedError;
      // return `Error (Status: ${status}): ${message}`;
      if (message) {
        return `${message}`;
      }
      if (error_message) {
        return `${error_message}`;
      }
    } catch (err) {
      if (typeof errorMessage === "string") {
        return errorMessage;
      } else {
        return "Something went wrong";
      }
    }
  };

  const returnBadgeClasses = (status) => {
    if (status === "Error") {
      return "bg-error-50 text-error-700 border-error-200";
    } else if (status === "Success") {
      return "bg-success-50 text-success-700 border-success-200";
    } else {
      return "bg-gray-50 text-gray-400 border-border ";
    }
  };
  const returnBadgeImg = (status) => {
    if (status === "Error") {
      return "-red";
    } else if (status === "Success") {
      return "-green";
    } else {
      return "";
    }
  };

  const returnTimelineBadge = (status = "", enrichment_mode = "", error = "") => {
    const emailModes = ["work_email", "personal_email", "email"];
    const phoneModes = ["mobile_phone", "direct_phone", "work_phone", "hq_phone", "phone"];

    return (
      <TooltipWrapper content={error} contentClassName="max-w-[200px]">
        <span
          className={`inline-flex items-center space-x-2 text-[12px] border ${returnBadgeClasses(status)} pl-3 pr-2 py-0.5 rounded-[20px] mr-1`}
        >
          {emailModes.includes(enrichment_mode) && (
            <Image className="h-5 w-5 no-invert" src={`/images/ext/email${returnBadgeImg(status)}.svg`} />
          )}
          {phoneModes.includes(enrichment_mode) && (
            <Image className="h-5 w-5 no-invert" src={`/images/ext/phone${returnBadgeImg(status)}.svg`} />
          )}
          <span>{status}</span>
        </span>
      </TooltipWrapper>
    );
    // if (status === "Error") {
    //   return (
    //     <>
    //       {parseErrorMessage(error) && (
    //         <span className="inline-flex text-[12px] bg-warning-50 text-warning-700 border border-warning-200 px-2.5 py-0.5 rounded-[20px] mr-1">
    //           {parseErrorMessage(error)}
    //         </span>
    //       )}
    //     </>
    //   );
    // } else if (status === "Not Found") {
    //   return (
    //     <TooltipWrapper content={`${enrichment_mode} not found`}>
    //       <span className="inline-flex items-center space-x-2 text-[12px] bg-gray-50 text-gray-400 border border-border pl-3 pr-2 py-0.5 rounded-[20px] mr-1">
    //         <span>{enrichment_mode}</span>
    //         <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    //           <path
    //             d="M8.34375 3.78125L3.65625 8.46875M3.65625 3.78125L8.34375 8.46875"
    //             stroke="var(--input)"
    //             stroke-width="1.40625"
    //             stroke-linecap="round"
    //             stroke-linejoin="round"
    //           />
    //         </svg>
    //       </span>
    //     </TooltipWrapper>
    //   );
    // } else if (status === "Success") {
    //   return (
    //     <TooltipWrapper content={`${enrichment_mode} found`}>
    //       <span className="inline-flex items-center space-x-2 text-[12px] bg-success-50 text-success-700 border border-success-200 pl-3 pr-2 py-0.5 rounded-[20px] mr-1">
    //         <span>{enrichment_mode}</span>
    //         <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    //           <path
    //             d="M9.875 3.3125L4.71875 8.46875L2.375 6.125"
    //             stroke="var(--success-500)"
    //             stroke-width="1.40625"
    //             stroke-linecap="round"
    //             stroke-linejoin="round"
    //           />
    //         </svg>
    //       </span>
    //     </TooltipWrapper>
    //   );
    // } else {
    //   return (
    //     <TooltipWrapper content={status ? status : ""}>
    //       <span className="inline-flex items-center space-x-2 text-[12px] bg-gray-50 text-gray-400 border border-border px-2.5 py-0.5 rounded-[20px] mr-1">
    //         <span>{status ? status : enrichment_mode ? enrichment_mode : ""}</span>
    //       </span>
    //     </TooltipWrapper>
    //   );
    // }
  };

  const [emailVerifyStatus, setEmailVerifyStatus] = useState("");
  const [customEmailVerify, setCustomEmailVerify] = useState(false);
  const emailsWithoutVerification = enrichedData?.email_addresses?.filter(
    (emailObj) => !("verification" in emailObj)
  );
  const emailsWithValidVerification = enrichedData?.email_addresses?.filter(
    (emailObj) => emailObj.verification?.result === "Valid"
  );

  const hasPendingInMobileAdvanceVerification =
    enrichedData &&
    enrichedData.phone_numbers &&
    enrichedData.phone_numbers.length > 0 &&
    enrichedData.phone_numbers.some(
      (item) =>
        item.verification &&
        item.verification.advanced_verification &&
        (item.verification.advanced_verification.status.toLowerCase() === "queued" ||
          item.verification.advanced_verification.status.toLowerCase() === "running")
    );

  const [showAdvanceVerificationAlert, setShowAdvanceVerificationAlert] = useState(true);

  return (
    <>
      {hasPendingInMobileAdvanceVerification && showAdvanceVerificationAlert && (
        <div className="border border-brand-500 bg-input p-4 mb-4 rounded-xl">
          <div className="flex items-start w-full justify-between">
            <div className="inline-flex items-start space-x-2">
              <Image
                className="no-invert"
                src="/images/ext/mobile-status/PrimeValid.svg"
                alt="Valid"
                width={18}
                height={18}
              />
              <div>
                <p className="text-text-700 text-xs font-semibold mb-1">
                  Finding phone ownership information
                </p>
                <p className="text-gray-600 text-xs">This might take a minute</p>
              </div>
            </div>
            <Image
              className="cursor-pointer opacity-45 no-invert"
              src="/images/ext/cross.svg"
              alt="Cancel"
              onClick={() => setShowAdvanceVerificationAlert(false)}
            />
          </div>
        </div>
      )}
      <div className="border   rounded-[14px] overflow-hidden border-input  bg-background relative">
        <div className="flex pt-4 pb-3 px-4 justify-between items-center ">
          <div>
            <span className="text-[16px] text-text-700 mr-3 font-semibold">Contact</span>
          </div>
        </div>
        {enrichedData &&
          enrichedData?.enrichment_lists &&
          enrichedData?.enrichment_lists.length > 0 &&
          enrichedData?.enrichment_lists[0].requirements &&
          enrichedData?.enrichment_lists[0].requirements.length > 0 &&
          getUniqueRequirements(enrichedData?.enrichment_lists).length === 2 &&
          !enrichProfileFn.isLoading &&
          !createContactsV2Fn.isLoading &&
          !getContactDetailsByIdFn.isLoading && (
            <div className="px-4 mb-2">
              {enrichedData?.enrichment_lists[0].requirements[0] === "work_email" && (
                <button
                  className="text-[14px] disabled:opacity-70 flex w-full justify-center items-center py-2 h-9 space-x-2 px-4 rounded-[8px] text-brand-600 bg-none border border-brand-600 font-semibold"
                  onClick={() => {
                    setEnrichmentModeSingle(true);
                    handleRevealContactDetailsClick(enrichedData.id, ["mobile_phone", "direct_phone"]);

                    captureEvent("EXP_Person_RevealContactBtn");
                  }}
                  disabled={enrichmentMode.length === 0}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.98356 6.71129C6.56356 7.91931 7.35422 9.05151 8.35553 10.0528C9.35685 11.0541 10.4891 11.8448 11.6971 12.4248C11.801 12.4747 11.8529 12.4996 11.9187 12.5188C12.1523 12.5869 12.4392 12.538 12.637 12.3963C12.6927 12.3564 12.7403 12.3088 12.8356 12.2135C13.1269 11.9222 13.2726 11.7765 13.4191 11.6813C13.9715 11.3221 14.6837 11.3221 15.2361 11.6813C15.3825 11.7765 15.5282 11.9222 15.8196 12.2135L15.9819 12.3759C16.4248 12.8188 16.6462 13.0402 16.7665 13.2781C17.0058 13.751 17.0058 14.3096 16.7665 14.7825C16.6462 15.0204 16.4248 15.2418 15.9819 15.6847L15.8506 15.816C15.4092 16.2574 15.1886 16.4781 14.8885 16.6466C14.5556 16.8336 14.0385 16.9681 13.6567 16.9669C13.3126 16.9659 13.0774 16.8992 12.607 16.7657C10.0792 16.0482 7.69387 14.6945 5.70388 12.7045C3.7139 10.7145 2.36017 8.32919 1.6427 5.80136C1.50919 5.33099 1.44244 5.0958 1.44141 4.75168C1.44028 4.36983 1.57475 3.85275 1.76176 3.51983C1.9303 3.2198 2.15098 2.99913 2.59233 2.55777L2.72369 2.42641C3.16656 1.98354 3.388 1.76211 3.62581 1.64182C4.09878 1.4026 4.65734 1.4026 5.1303 1.64182C5.36812 1.76211 5.58955 1.98354 6.03242 2.42641L6.19481 2.5888C6.48615 2.88014 6.63182 3.02581 6.72706 3.17229C7.08622 3.7247 7.08622 4.43685 6.72706 4.98926C6.63182 5.13574 6.48615 5.28141 6.19481 5.57275C6.09955 5.66801 6.05192 5.71564 6.01206 5.77132C5.87038 5.96917 5.82146 6.25606 5.88957 6.48968C5.90873 6.55543 5.93367 6.60738 5.98356 6.71129Z"
                      stroke="var(--brand-700)"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <span>Reveal Phone</span>
                  {(enrichProfileFn.isLoading || createContactsV2Fn.isLoading) && (
                    <div className="loader_circle inline-block"></div>
                  )}
                </button>
              )}
              {enrichedData?.enrichment_lists[0].requirements[0] === "mobile_phone" && (
                <button
                  className="text-[14px] disabled:opacity-70 flex w-full justify-center items-center py-2 h-9 space-x-2 px-4 rounded-[8px] text-brand-600 bg-none border border-brand-600 font-semibold"
                  onClick={() => {
                    setEnrichmentModeSingle(true);
                    handleRevealContactDetailsClick(enrichedData.id, ["work_email", "personal_email"]);
                  }}
                  disabled={enrichmentMode.length === 0}
                >
                  <svg
                    width="19"
                    height="16"
                    viewBox="0 0 19 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.16675 4.16699L7.97085 8.92986C8.52182 9.31554 8.79731 9.50839 9.09697 9.58308C9.36166 9.64906 9.6385 9.64906 9.9032 9.58308C10.2029 9.50839 10.4783 9.31555 11.0293 8.92986L17.8334 4.16699M5.16675 15.0003H13.8334C15.2335 15.0003 15.9336 15.0003 16.4684 14.7278C16.9388 14.4882 17.3212 14.1057 17.5609 13.6353C17.8334 13.1005 17.8334 12.4005 17.8334 11.0003V5.66699C17.8334 4.26686 17.8334 3.5668 17.5609 3.03202C17.3212 2.56161 16.9388 2.17916 16.4684 1.93948C15.9336 1.66699 15.2335 1.66699 13.8334 1.66699H5.16675C3.76662 1.66699 3.06655 1.66699 2.53177 1.93948C2.06137 2.17916 1.67892 2.56161 1.43923 3.03202C1.16675 3.5668 1.16675 4.26686 1.16675 5.66699V11.0003C1.16675 12.4005 1.16675 13.1005 1.43923 13.6353C1.67892 14.1057 2.06137 14.4882 2.53177 14.7278C3.06655 15.0003 3.76662 15.0003 5.16675 15.0003Z"
                      stroke="var(--brand-700)"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <span>Reveal Email</span>
                  {(enrichProfileFn.isLoading || createContactsV2Fn.isLoading) && (
                    <div className="loader_circle inline-block"></div>
                  )}
                </button>
              )}
            </div>
          )}
        <div className="absolute left-0 z-10 w-full top-0">{showStripeLoader()}</div>
        {enrichedData &&
          enrichedData?.enrichment_lists &&
          enrichedData?.enrichment_lists.length > 0 &&
          isMoreThanDaysOld(enrichedData?.enrichment_lists[0].created_at, 7) && (
            <div className="px-4">
              {showReEnrichCard && (
                <div className="p-4 text-[12px] rounded-xl border boder-input bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="inline-flex items-center space-x-2">
                      <Checkbox
                        id="for__email"
                        checked={enrichmentModeEmail}
                        onCheckedChange={(checked) => {
                          setEnrichmentModeEmail(checked);
                        }}
                      />
                      <label className="text-text-700 text-sm">Email</label>
                    </div>
                    <div className="inline-flex items-center space-x-2">
                      <Checkbox
                        id="for__phone"
                        checked={enrichmentModePhone}
                        onCheckedChange={(checked) => {
                          setEnrichmentModePhone(checked);
                        }}
                      />
                      <label className="text-text-700 text-sm">Phone</label>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between space-x-2">
                    <button
                      className="text-brand-700 bg-background w-[70%] font-bold rounded-[8px] text-[14px] border border-brand-700 h-9 px-1 inline-flex items-center justify-center"
                      onClick={() => handleReEnrichContactDetailsClick(enrichedData?.id, enrichmentMode)}
                      disabled={enrichProfileFn.isLoading || getContactDetailsByIdFn.isLoading}
                    >
                      <ReloadIcon className="mr-2" width={20} height={20} />
                      <span>Refetch</span>
                      {enrichProfileFn.isLoading && <div className="loader_circle inline-block ml-1"></div>}
                    </button>
                    <button
                      className="text-gray-700 bg-background font-semibold rounded-[8px] text-[12px] border border-border h-9 inline-flex items-center justify-center px-6"
                      onClick={() => setShowReEnrichCard(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {!showReEnrichCard && (
                <button
                  className="text-gray-700 bg-background font-semibold rounded-[8px] text-[12px] border border-border h-9 px-1 flex w-full items-center justify-center"
                  onClick={() => setShowReEnrichCard(true)}
                >
                  <ReloadIcon className="mr-1" />
                  <span>
                    Refresh data{" "}
                    <span className="text-[11px]">
                      (Last fetched{" "}
                      {dayjs(enrichedData?.enrichment_lists[0]?.created_at).format("D MMM YYYY")})
                    </span>
                  </span>
                </button>
              )}
            </div>
          )}

        <div className="pb-4 pt-1 px-4 space-y-5">
          {/* {
            // enrichProfileFn.isSuccess &&
            enrichedData &&
              emailsWithoutVerification &&
              emailsWithValidVerification &&
              emailsWithValidVerification.length === 0 &&
              emailsWithoutVerification.length > 0 && (
                <>
                  {!userData?.preferences?.chrome_ext_auto_verify_email &&
                    !emailVerifyStatus && (
                      <ContactAlert
                        heading="Contact details are unverified"
                        type="warning"
                      >
                        <span
                          className="text-brand-700 cursor-pointer text-xs font-bold"
                          onClick={() => {
                            setEmailVerifyStatus("loading");
                            setCustomEmailVerify(true);
                          }}
                        >
                          Verify details
                        </span>
                      </ContactAlert>
                    )}
                  {(userData?.preferences?.chrome_ext_auto_verify_email ||
                    emailVerifyStatus) && (
                    <>
                      {emailVerifyStatus === "error" && (
                        <ContactAlert
                          heading="Email verification not available"
                          description="No email verification integrations available"
                          type="email"
                        >
                          <a
                            target="_blank"
                            href={`${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/integrations`}
                            className="text-brand-700 cursor-pointer text-xs font-bold flex items-center"
                          >
                            Connect providers{" "}
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="ml-2"
                            >
                              <path
                                d="M17.5 7.50001L17.5 2.50001M17.5 2.50001H12.5M17.5 2.50001L10 10M8.33333 2.5H6.5C5.09987 2.5 4.3998 2.5 3.86502 2.77248C3.39462 3.01217 3.01217 3.39462 2.77248 3.86502C2.5 4.3998 2.5 5.09987 2.5 6.5V13.5C2.5 14.9001 2.5 15.6002 2.77248 16.135C3.01217 16.6054 3.39462 16.9878 3.86502 17.2275C4.3998 17.5 5.09987 17.5 6.5 17.5H13.5C14.9001 17.5 15.6002 17.5 16.135 17.2275C16.6054 16.9878 16.9878 16.6054 17.2275 16.135C17.5 15.6002 17.5 14.9001 17.5 13.5V11.6667"
                                stroke="var(--brand-700)"
                                stroke-width="1.66667"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </a>
                        </ContactAlert>
                      )}
                      {emailVerifyStatus === "loading" && (
                        <ContactAlert
                          heading="Verifying contact details..."
                          description="This might take a couple of minutes"
                          type="verify"
                          loading={true}
                        />
                      )}
                      {emailVerifyStatus === "success" && (
                        <ContactAlert
                          heading="Contact details are verified"
                          type="verify"
                        />
                      )}
                    </>
                  )}
                </>
              )
          } */}

          <CrmContacts crmPhonesEmails={crmPhonesEmails} />

          {enrichedData && enrichedData?.enrichment_lists && enrichedData?.enrichment_lists.length > 0 ? (
            <div>
              <div className="pl-1">
                {/* <div className="flex items-center">
                  <span className="inline-flex w-6 text-center">
                    <Image className="no-invert" src="/images/ext/phone.svg" alt="Phone" />
                  </span>
                  <span className="text-[12px] text-gray-900">Phone</span>
                </div> */}
                {enrichedData?.phone_numbers && enrichedData?.phone_numbers.length > 0 ? (
                  enrichedData?.phone_numbers.map((number, index) => (
                    <>
                      <EnrichedPhone
                        number={number}
                        type={number?.type}
                        phoneValue={number?.value}
                        isValid={
                          number?.verification?.status !== "Completed" ? null : number?.verification?.is_valid
                        }
                        lineType={number?.verification?.line_type}
                        foundFrom={{
                          name: number?.integration_config?.integration?.display_name,
                          key: number?.integration_config?.integration?.key,
                        }}
                        feedback={number?.feedback}
                        value={
                          number?.verification?.status === "Completed"
                            ? number?.verification?.international_format
                              ? number?.verification?.international_format
                              : number?.value
                            : number?.value
                        }
                        activeWorkspace={activeWorkspace}
                        contactId={enrichedData.id}
                        onSuccess={() => {
                          getContactDetailsByIdFn.mutate({
                            id: enrichedData.id,
                            options: {
                              headers: {
                                "X-Workspace-Id": activeWorkspace?.id,
                              },
                            },
                          });
                        }}
                        showDevider={index + 1 !== enrichedData?.phone_numbers.length ? true : false}
                        advanceVerification={{
                          verified_at_simple: number?.verification?.verified_at
                            ? dayjs(number?.verification?.verified_at).format("hh:mm A, MM/DD/YY")
                            : "",
                          status: number?.verification?.advanced_verification?.status,
                          verification_status:
                            number?.verification?.advanced_verification?.verification_status,
                          verified_at: number?.verification?.advanced_verification?.verified_at
                            ? dayjs(number?.verification?.advanced_verification?.verified_at).format(
                                "hh:mm A, MM/DD/YY"
                              )
                            : "",
                        }}
                      />
                    </>
                  ))
                ) : (
                  <>
                    <>
                      {shouldShowBlur() ? (
                        <span className="text-[12px] block mt-2 blur-[2px] pointer-events-none unselectable">
                          +1 9876543210
                        </span>
                      ) : (
                        <>
                          {getContactDetailsByIdFn.isLoading || isEnrichmentTimelineRunning() ? (
                            <Skeleton className="h-[20px] w-7/12 mt-2" />
                          ) : (
                            <span className="text-[12px] block mt-2">No phones found.</span>
                          )}
                        </>
                      )}
                    </>
                  </>
                )}
              </div>
              <div className="pl-1">
                <div className="w-[240px] h-[1px] bg-border mx-auto my-3"></div>
                {/* <div className="flex items-center">
                  <span className="inline-flex w-6 text-center">
                    <Image className="no-invert" src="/images/ext/email.svg" alt="Email" />
                  </span>
                  <span className="text-[12px] text-gray-900">Email</span>
                </div> */}
                {enrichedData?.email_addresses && enrichedData?.email_addresses.length > 0 ? (
                  enrichedData?.email_addresses.map((email, index) => (
                    <>
                      <EnrichedEmail
                        email={email}
                        contactId={enrichedData.id}
                        feedback={email?.feedback}
                        foundFrom={{
                          name: email?.integration_config?.integration?.display_name,
                          key: email?.integration_config?.integration?.key,
                        }}
                        value={email?.value}
                        badge={email?.verification && email?.verification?.result}
                        activeWorkspace={activeWorkspace}
                        onSuccess={() => {
                          getContactDetailsByIdFn.mutate({
                            id: enrichedData.id,
                            options: {
                              headers: {
                                "X-Workspace-Id": activeWorkspace?.id,
                              },
                            },
                          });
                        }}
                        autoVerify={userData?.preferences?.chrome_ext_auto_verify_email ? true : false}
                        customEmailVerify={customEmailVerify}
                        customEmailVerifyToast={
                          enrichedData && emailsWithoutVerification && emailsWithoutVerification.length > 0
                            ? true
                            : false
                        }
                        type={email?.type}
                        showDevider={index + 1 !== enrichedData?.email_addresses.length ? true : false}
                        isVerifying={(status) => {
                          setEmailVerifyStatus(status);
                        }}
                      />
                    </>
                  ))
                ) : (
                  <>
                    <>
                      {shouldShowBlur() ? (
                        <span className="text-[12px] block mt-2 blur-[2px] pointer-events-none unselectable">
                          reveal@example.com
                        </span>
                      ) : (
                        <>
                          {getContactDetailsByIdFn.isLoading || isEnrichmentTimelineRunning() ? (
                            <Skeleton className="h-[20px] w-7/12 mt-2" />
                          ) : (
                            <span className="text-[12px] block mt-2">No emails found.</span>
                          )}
                        </>
                      )}
                    </>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {installedDataProviderIntegrations && installedDataProviderIntegrations.length > 0 && (
                <div className="flex items-center space-x-4">
                  <div className="inline-flex items-center space-x-2">
                    <Checkbox
                      id="for__email"
                      checked={enrichmentModeEmail}
                      onCheckedChange={(checked) => {
                        setEnrichmentModeEmail(checked);
                      }}
                    />
                    <label className="text-text-700 text-sm">Email</label>
                  </div>
                  <div className="inline-flex items-center space-x-2">
                    <Checkbox
                      id="for__phone"
                      checked={enrichmentModePhone}
                      onCheckedChange={(checked) => {
                        setEnrichmentModePhone(checked);
                      }}
                    />
                    <label className="text-text-700 text-sm">Phone</label>
                  </div>
                </div>
              )}
            </>
          )}

          {enrichedData &&
            enrichedData?.enrichment_lists &&
            enrichedData?.enrichment_lists.length === 0 &&
            installedDataProviderIntegrations.length > 0 && (
              <>
                {enrichmentMode.length === 0 && (
                  <p className="text-red-700 text-[12px] mt-2">
                    Atleast select Email or Phone to Reveal contact details
                  </p>
                )}
                <div>
                  <button
                    className="text-[14px] disabled:opacity-70 flex w-full justify-center items-center py-2 h-9 space-x-2 px-4 rounded-[8px] text-white bg-brand-600 dark:bg-brand-300 border border-brand-600 font-semibold"
                    onClick={() => {
                      if (enrichedData && enrichedData.fromPersonId) {
                        createContactsV2Fn.isLoading ? void 0 : handleCreateContactsV2(params.person_id);
                      }
                      if (enrichedData && enrichedData.fromContactId) {
                        enrichProfileFn.isLoading ? void 0 : handleRevealContactDetailsClick(enrichedData.id);
                      }

                      captureEvent("EXP_Person_RevealContactBtn");
                    }}
                    disabled={enrichmentMode.length === 0}
                  >
                    <span>Reveal contact details</span>
                    {(enrichProfileFn.isLoading || createContactsV2Fn.isLoading) && (
                      <div className="loader_circle inline-block"></div>
                    )}
                  </button>
                </div>
              </>
            )}

          {enrichedData &&
            enrichedData?.enrichment_lists &&
            enrichedData?.enrichment_lists.length === 0 &&
            installedDataProviderIntegrations.length === 0 && (
              <div>
                <a
                  className="text-[14px] flex w-full justify-center items-center py-2 h-9 space-x-2 px-4 rounded-[8px] text-white bg-brand-600 border border-brand-600 font-semibold"
                  target="_blank"
                  href={`${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace.slug}/settings/integrations`}
                  onClick={() => {
                    captureEvent("EXP_Person_AddIntegrationToReveal");
                  }}
                >
                  <span>Add an integration to reveal</span>
                </a>
              </div>
            )}
        </div>
        {enrichedData && enrichedData?.enrichment_lists && enrichedData?.enrichment_lists.length > 0 && (
          <>
            <div className="w-[240px] h-[1px] bg-border mx-auto mt-2 mb-1"></div>
            <div className={`py-4 px-4 space-y-5 ${toggleEnrichData && "pb-0"}`}>
              <h3
                className="text-[14px] font-semibold flex justify-center items-center cursor-pointer"
                onClick={handleToggleEnrichData}
              >
                <span className="inline-flex text-gray-600">
                  {getContactDetailsByIdFn.isLoading ||
                  enrichedData.enrichment_lists.some(
                    (item) =>
                      item.status.toLowerCase() === "queued" || item.status.toLowerCase() === "running"
                  ) ? (
                    <div className="inline-flex items-center">
                      <div className="loader_circle inline-block mr-2"></div>
                      Fetching Contact Info
                    </div>
                  ) : (
                    <>
                      {enrichedList && enrichedList.length > 0 && filterByFound(enrichedList).length > 0 ? (
                        <>
                          <Image src="/images/ext/multi_found_info.svg" className="mr-1 no-invert" />
                          Fetched from {filterByFound(enrichedList).length}{" "}
                          {filterByFound(enrichedList).length === 1 ? "provider" : "providers"}
                        </>
                      ) : (
                        <span className=" inline-flex items-center">
                          {/* <img
                              src="images/ext/multi_found.svg"
                              className="mr-1 no-invert"
                            />
                            Enrichment Timeline */}
                        </span>
                      )}
                    </>
                  )}
                </span>
                {toggleEnrichData ? <ChevronUpIcon className="ml-2" /> : <ChevronDownIcon className="ml-2" />}
              </h3>

              {toggleEnrichData &&
                enrichedList &&
                enrichedList.length > 0 &&
                enrichedList.map((item, itemIndex) => (
                  <div className={enrichedList.length !== itemIndex + 1 && "border-b pb-2"}>
                    <div className="flex items-center mb-3 justify-between">
                      <div className="inline-flex items-center">
                        <div className="h-8 w-8 flex-shrink-0">
                          {item.created_by && (
                            <Image
                              className="h-8 w-8 rounded-full bg-gray-50 border border-border p-1.5 no-invert"
                              src={"/images/icons/untitledui/user.svg"}
                              alt=""
                            />
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="font-bold text-foreground">{item.created_by?.name}</div>
                          <div className="mt-0 text-paragraph">{dayjs(item?.created_at).fromNow()}</div>
                        </div>
                      </div>
                      {item && item.tasks && item.tasks.length > 0 && (
                        <TooltipWrapper content={`Credit consumed: ${item.tasks[0]?.credit_consumed}`}>
                          {item.tasks[0]?.credit_consumed ? (
                            <Image
                              className="no-invert"
                              src={"/images/ext/credit-consumed-orange.svg"}
                              alt=""
                            />
                          ) : (
                            <Image
                              className="no-invert"
                              src={"/images/ext/credit-consumed-grey.svg"}
                              alt=""
                            />
                          )}
                        </TooltipWrapper>
                      )}
                    </div>
                    <div className="flow-root text-[12px] slide-in">
                      <ul role="list">
                        {item &&
                          item.tasks &&
                          item.tasks.length > 0 &&
                          item.tasks[0].actions &&
                          item.tasks[0].actions.length > 0 &&
                          groupByIntegrationWithMapping(item.tasks[0].actions).map((timeline, index) => (
                            <li>
                              <div className="relative pb-3">
                                {groupByIntegrationWithMapping(item.tasks[0].actions).length !==
                                  index + 1 && (
                                  <span
                                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                    aria-hidden="true"
                                  ></span>
                                )}
                                <div className="relative">
                                  <div className="flex space-x-3 items-center">
                                    <TooltipWrapper
                                      content={timeline?.integration_config?.integration?.display_name}
                                    >
                                      <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                        {timeline?.integration_config?.integration ? (
                                          <Image
                                            className="no-invert"
                                            src={`/images/ext/providers/${timeline?.integration_config?.integration?.key}.svg`}
                                            alt=""
                                            style={{ width: "auto", height: 19 }}
                                          />
                                        ) : (
                                          <svg
                                            className="h-5 w-5 text-white"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                          >
                                            <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
                                          </svg>
                                        )}
                                      </span>
                                    </TooltipWrapper>
                                    <span className="text-[13px] text-text-700 font-semibold">
                                      {timeline?.integration_config?.integration?.display_name}
                                      {/* <span className="inline-block text-[10px] font-normal ml-2">
                                        <TooltipWrapper
                                          content={
                                            item?.created_at ? dayjs(item?.created_at).format("LLLL") : ""
                                          }
                                        >
                                          {item?.created_at ? dayjs(item?.created_at).fromNow() : ""}
                                        </TooltipWrapper>
                                      </span> */}
                                    </span>
                                  </div>
                                  <div className="pl-11 mt-0">
                                    {timeline &&
                                      timeline.mapping &&
                                      timeline.mapping.length > 0 &&
                                      timeline.mapping.map((data) => (
                                        <div className="inline-flex mb-2">
                                          {returnTimelineBadge(
                                            data?.status,
                                            data?.requirements?.[0],
                                            data?.error_message
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
        {enrichedData && enrichedData?.enrichment_lists && enrichedData?.enrichment_lists.length === 0 && (
          <div>
            <div className="w-[240px] h-[1px] bg-border mx-auto my-2"></div>
            <div className="p-4 ">
              <h3
                className="text-[15px] font-semibold flex justify-center items-center cursor-pointer mb-1 text-gray-600"
                onClick={() => {
                  handleToggleIntegration();
                  captureEvent("EXP_Person_ExpandProviders");
                }}
              >
                <>
                  {installedDataProviderIntegrations.length > 0 ? (
                    <span className="inline-flex space-x-2 items-center text-gray-600">
                      {/* <span className="leading-none">
                          {installedIntegrations.length} provider
                          {installedIntegrations.length > 1 && "s"} connected
                        </span> */}
                      {/* {installedIntegrations.length > 0 && (
                            <>
                              {installedIntegrations
                                .slice(0, 2)
                                .map((integration) => (
                                  <span
                                    key={integration.id}
                                    className="integration__svg__logo"
                                    dangerouslySetInnerHTML={{
                                      __html: integration.logo,
                                    }}
                                  />
                                ))}
                              {installedIntegrations.length > 2 && (
                                <span className="font-normal text-[12px] bg-slate-100 rounded px-1">
                                  +{installedIntegrations.length - 2}
                                </span>
                              )}
                            </>
                          )} */}
                    </span>
                  ) : (
                    <span className="inline-flex">
                      <Image src="/images/ext/theme/int_install.svg" className="mr-1 no-invert" />{" "}
                      Integrations available!
                    </span>
                  )}
                  <span className="ml-2">
                    <ChevronDownIcon />
                  </span>
                </>
              </h3>
              {/* <span className="text-gray-500 text-[12px]">
              Integrate with a data provider to enrich contacts
            </span> */}
            </div>
            {toggleIntegation && (
              <div className="px-4 flex flex-wrap -mx-2 slide-in">
                {integrations &&
                  integrations.available &&
                  integrations.available.length > 0 &&
                  integrations.available
                    .filter((integration) => {
                      if (restrictedIntegrations.includes(integration.key)) {
                        return isPremiumProvidersEnabled;
                      }
                      if (integration.key === "advanced_phone_verification") {
                        return isAdvancePhoneVerificationEnabled;
                      }
                      return true;
                    })
                    .map((integration) => (
                      <div className="items-center w-3/12 mb-4 px-2">
                        <Tooltip>
                          <TooltipTrigger className="p-0 border-0 bg-none text-gray-500">
                            <a
                              className={`inline-flex justify-center items-center flex-col border  relative overflow-hidden ${
                                integration.installed ? "border-success-600 bg-success-100" : "border-border"
                              } p-3.5 px-3.5 rounded-[8px] h-[58px] shadow-sm bg-background dark:bg-gray-900`}
                              href={`${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${
                                activeWorkspace?.slug
                              }/settings/integrations?tab=integrations&${
                                integration.installed ? "manage" : "connect"
                              }=${integration.key}`}
                              target="_blank"
                            >
                              <span
                                className="integration__svg__logo_big"
                                dangerouslySetInnerHTML={{
                                  __html: integration.logo,
                                }}
                              />
                              {/* {integration.installed && (
                        <Tooltip>
                          <TooltipTrigger className="p-0 border-0 bg-none text-gray-500">
                            <CheckIcon
                              className="absolute top-0 right-0 bg-success-600 text-white rounded-bl"
                              width={13}
                              height={13}
                            />
                          </TooltipTrigger>
                          <TooltipContent>Installed</TooltipContent>
                        </Tooltip>
                      )} */}
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>{integration.display_name}</TooltipContent>
                        </Tooltip>
                        {/* <h3 className="text-[12px] text-gray-900 text-center mt-2">
                      {integration.display_name}
                    </h3> */}
                      </div>
                    ))}
              </div>
            )}
          </div>
        )}
      </div>
      {getEnrichedDataBypersonIdFn.isSuccess && activeWorkspace && activeWorkspace.id && params && (
        <IcpPersona
          personId={params?.person_id}
          contactId={contactId ? contactId : ""}
          workspaceId={activeWorkspace.id}
        />
      )}
      {enrichedData && enrichedData?.icp_ratings && enrichedData?.icp_ratings.length > 0 && (
        <div className="pt-2">
          <ICPRating icpData={enrichedData?.icp_ratings[0]} />
        </div>
      )}
      {/* {enrichedData &&
        params &&
        params.contact_id &&
        userData &&
        userData.activeWorkspace && (
          <div className="pt-2">
            <KeyTakeaways
              contact_id={params.contact_id}
              activeWorkspace={userData.activeWorkspace}
              key_points={enrichedData?.key_points}
            />
          </div>
        )} */}
    </>
  );
}
